import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { orderAPI, paymentAPI, authAPI, promoCodeAPI, clientContactsAPI } from '../../services/api';
import { addressService } from '../../services/addressService';
import { formatFullName } from '../../utils/nameUtils';
import { InputLabel, Select, MenuItem } from '@mui/material';
import { calculateTieredPrice } from '../../utils/tieredPricing';

const Checkout = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const cartItems = location.state?.cartItems || [];

  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Address management
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedShippingId, setSelectedShippingId] = useState('');
  const [useNewShipping, setUseNewShipping] = useState(false);

  // Shipping address
  const [shippingAddress, setShippingAddress] = useState({
    name: formatFullName(currentUser) || '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: currentUser?.phone || '',
  });

  // Billing address
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] = useState({
    name: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
  });

  // Shipping options
  const [shippingMethod, setShippingMethod] = useState('FEDEX_FIRST_OVERNIGHT');
  const [deliveryDate, setDeliveryDate] = useState('');

  // All orders now use Net 60 terms (payment options removed)
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoDetails, setPromoDetails] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [validatingPromo, setValidatingPromo] = useState(false);

  // Ordering vet selection (for clinics with multiple vets)
  const [vetContacts, setVetContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [orderingVetContactId, setOrderingVetContactId] = useState(''); // Empty string means "General/Bulk Order"
  
  // Form validation
  const [errors, setErrors] = useState({});

  const steps = ['Shipping Information', 'Delivery Options', 'Review & Submit'];

  // Calculate total quantity for tiered pricing
  const totalQuantity = useMemo(() => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  }, [cartItems]);

  // Calculate tiered price per unit based on total quantity
  const tieredPricePerUnit = useMemo(() => {
    return calculateTieredPrice(totalQuantity);
  }, [totalQuantity]);

  // Check if bulk pricing is applied
  const isBulkPricing = useMemo(() => {
    return totalQuantity >= 20;
  }, [totalQuantity]);

  // Calculate order totals with tiered pricing
  const calculateTotals = () => {
    // Use tiered pricing for all items
    const subtotal = cartItems.reduce((total, item) => {
      return total + (tieredPricePerUnit * item.quantity);
    }, 0);
    
    // Apply promo discount if available
    let promoDiscount = 0;
    if (promoApplied && promoDetails) {
      promoDiscount = promoDetails.discountAmount;
    }
    
    const subtotalAfterPromo = subtotal - promoDiscount;
    
    // No tax charged
    const taxAmount = 0;
    
    // Shipping costs - all overnight shipping is free
    let shippingAmount = 0;
    
    // Calculate discount percent
    const discountPercent = promoDiscount > 0 ? (promoDiscount / subtotal * 100) : 0;
    const discountAmount = promoDiscount;
    
    // Total is subtotal minus promo discount (no tax, no shipping)
    const totalAmount = subtotalAfterPromo;

    return {
      subtotal,
      promoDiscount,
      subtotalAfterPromo,
      taxAmount,
      shippingAmount,
      discountPercent,
      discountAmount,
      totalAmount
    };
  };

  const totals = calculateTotals();

  // Get minimum delivery date (no same-day, no next-day after 3 PM EST)
  const getMinDeliveryDate = () => {
    const now = new Date();
    const estTime = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}));
    const currentHour = estTime.getHours();
    
    // If after 3 PM EST, add 2 days. Otherwise add 1 day.
    const daysToAdd = currentHour >= 15 ? 2 : 1;
    
    const minDate = new Date(estTime);
    minDate.setDate(minDate.getDate() + daysToAdd);
    
    return minDate.toISOString().split('T')[0];
  };

  // Validate shipping address
  const validateShippingAddress = () => {
    const newErrors = {};
    
    if (!shippingAddress.name.trim()) newErrors.shippingName = 'Name is required';
    if (!shippingAddress.address1.trim()) newErrors.shippingAddress1 = 'Street address is required';
    if (!shippingAddress.city.trim()) newErrors.shippingCity = 'City is required';
    if (!shippingAddress.state.trim()) newErrors.shippingState = 'State is required';
    if (!shippingAddress.zipCode.trim()) newErrors.shippingZipCode = 'ZIP code is required';
    if (shippingAddress.zipCode && !/^\d{5}(-\d{4})?$/.test(shippingAddress.zipCode)) {
      newErrors.shippingZipCode = 'Invalid ZIP code format';
    }
    if (!shippingAddress.phone.trim()) newErrors.shippingPhone = 'Phone is required';
    if (shippingAddress.phone && !/^\d{10}$/.test(shippingAddress.phone.replace(/\D/g, ''))) {
      newErrors.shippingPhone = 'Invalid phone format (10 digits required)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate billing address
  const validateBillingAddress = () => {
    if (billingSameAsShipping) return true;
    
    const newErrors = {};
    
    if (!billingAddress.name.trim()) newErrors.billingName = 'Name is required';
    if (!billingAddress.address1.trim()) newErrors.billingAddress1 = 'Street address is required';
    if (!billingAddress.city.trim()) newErrors.billingCity = 'City is required';
    if (!billingAddress.state.trim()) newErrors.billingState = 'State is required';
    if (!billingAddress.zipCode.trim()) newErrors.billingZipCode = 'ZIP code is required';
    if (billingAddress.zipCode && !/^\d{5}(-\d{4})?$/.test(billingAddress.zipCode)) {
      newErrors.billingZipCode = 'Invalid ZIP code format';
    }

    setErrors({...errors, ...newErrors});
    return Object.keys(newErrors).length === 0;
  };

  // Validate delivery information
  const validateDeliveryAndPayment = () => {
    const newErrors = {};
    
    if (!deliveryDate) newErrors.deliveryDate = 'Delivery date is required';
    if (!shippingMethod) newErrors.shippingMethod = 'Shipping method is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    let isValid = false;

    if (activeStep === 0) {
      isValid = validateShippingAddress() && validateBillingAddress();
    } else if (activeStep === 1) {
      isValid = validateDeliveryAndPayment();
    }

    if (isValid) {
      setActiveStep((prevStep) => prevStep + 1);
      window.scrollTo(0, 0);
    }
  };

  // Handle back
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo(0, 0);
  };
  
  // Promo code validation
  const handleValidatePromo = async () => {
    if (!promoCode) return;
    
    setValidatingPromo(true);
    setPromoError('');
    
    try {
      const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
      const primaryProduct = cartItems[0]; // Assuming single product or using first item
      
      const response = await promoCodeAPI.validatePromoCode({
        code: promoCode,
        productId: primaryProduct?.productId || primaryProduct?.sku,
        quantity: totalQuantity,
        basePrice: tieredPricePerUnit,
        organization: currentUser?.organization || 'EPL'
      });
      
      if (response.success && response.valid) {
        setPromoApplied(true);
        setPromoDetails(response.data);
        setPromoError('');
      } else {
        setPromoError(response.message || 'Invalid promo code');
        setPromoApplied(false);
        setPromoDetails(null);
      }
    } catch (error) {
      console.error('Error validating promo:', error);
      setPromoError(error.message || 'Failed to validate promo code');
      setPromoApplied(false);
      setPromoDetails(null);
    } finally {
      setValidatingPromo(false);
    }
  };
  
  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoApplied(false);
    setPromoDetails(null);
    setPromoError('');
  };

  // Submit order
  const handleSubmitOrder = async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Create the order first
      // Find the selected vet for notes
      const selectedVet = vetContacts.find(v => v.id === orderingVetContactId);
      const vetName = selectedVet ? 
        `${selectedVet.prefix || ''} ${selectedVet.firstName} ${selectedVet.lastName}${selectedVet.suffix ? `, ${selectedVet.suffix}` : ''}`.trim() : 
        null;
      
      const orderData = {
        items: cartItems.map(item => ({
          productId: item.productId || item.sku,
          quantity: item.quantity
        })),
        shippingAddress: {
          ...shippingAddress,
        },
        billingAddress: billingSameAsShipping ? shippingAddress : billingAddress,
        billingSameAsShipping,
        shippingMethod,
        deliveryDate,
        paymentTerms: 'NET_60', // All orders use Net 60 terms
        discountPercent: promoApplied && promoDetails ? (promoDetails.discountAmount / totals.subtotal * 100) : 0,
        promoCode: promoApplied ? promoCode : null,
        promoCodeId: promoApplied ? promoDetails?.promoCodeId : null,
        promoDiscount: promoApplied ? promoDetails?.discountAmount : 0,
        orderingVetContactId: orderingVetContactId || null, // Which vet is ordering
        isCompanyStock: !orderingVetContactId, // True if general/bulk order
        notes: `${vetName ? `Ordering vet: ${vetName}. ` : 'General practice stock order. '}Delivery requested: ${deliveryDate}. Shipping method: ${shippingMethod}. Net 60 payment terms with 10% early payment discount available.${promoApplied ? ` Promo code ${promoCode} applied.` : ''}`
      };

      const orderResponse = await orderAPI.createOrder(orderData);
      if (!orderResponse.success) {
        throw new Error(orderResponse.error || 'Failed to create order');
      }
      
      // Step 1.5: Apply promo code if present
      if (promoApplied && promoDetails && orderResponse.data) {
        try {
          await promoCodeAPI.applyPromoCode({
            code: promoCode,
            orderId: orderResponse.data.id,
            productId: cartItems[0]?.productId || cartItems[0]?.sku,
            quantity: cartItems.reduce((sum, item) => sum + item.quantity, 0),
            originalAmount: totals.subtotal,
            discountAmount: promoDetails.discountAmount,
            finalAmount: totals.totalAmount
          });
        } catch (promoError) {
          console.error('Error applying promo code:', promoError);
          // Don't fail the order if promo application fails, just log it
        }
      }

      // Step 2: Create Hyfin invoice for all orders
      const invoiceData = {
        orderId: orderResponse.data.id,
        orderNumber: orderResponse.data.orderNumber || `ORD-${orderResponse.data.id.slice(-6)}`,
        customer: {
          firstName: shippingAddress.name.split(' ')[0] || 'Customer',
          lastName: shippingAddress.name.split(' ').slice(1).join(' ') || 'Name',
          email: 'customer@example.com', // TODO: Collect email in checkout
          phone: shippingAddress.phone,
          address: {
            street1: shippingAddress.address1,
            street2: shippingAddress.address2 || '',
            city: shippingAddress.city,
            state: shippingAddress.state,
            zip: shippingAddress.zipCode,
            country: 'US'
          }
        },
        items: cartItems.map(item => ({
          productId: item.productId || item.sku,
          productName: item.name || item.productName || 'EPL Product',
          unitPrice: tieredPricePerUnit,
          quantity: item.quantity,
          total: tieredPricePerUnit * item.quantity
        })),
        totals: {
          subtotal: totals.subtotal,
          tax: 0, // No tax charged
          shipping: 0, // No shipping charged
          total: totals.totalAmount
        },
        deliveryDate,
        shippingMethod,
        notes: `Delivery requested: ${deliveryDate}. Shipping: ${shippingMethod === '2DAY' ? '2-Day Delivery' : 'Overnight Delivery'}. Net 60 payment terms with 10% early payment discount available within 2 days.`
      };

      const invoiceResult = await paymentAPI.createInvoice(invoiceData);
      if (!invoiceResult.success) {
        throw new Error(invoiceResult.error || 'Failed to create invoice');
      }

      setSuccess(true);
      
      // Redirect to confirmation page after 2 seconds
      setTimeout(() => {
        navigate('/client/confirmation', {
          state: {
            order: orderResponse.data,
            invoice: invoiceResult.data,
            message: 'Order created with Net 60 terms! Pay within 2 days for 10% discount or standard 60-day terms apply.'
          }
        });
      }, 2000);

    } catch (err) {
      console.error('Order submission error:', err);
      setError(err.message || 'Failed to process order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load saved addresses and redirect if no cart items
  useEffect(() => {
    if (!cartItems || cartItems.length === 0) {
      navigate('/client/place-order');
      return;
    }
    
    // Fetch user profile and sync address
    const syncProfileAddress = async () => {
      try {
        const response = await authAPI.getCurrentUser();
        if (response.success && response.data) {
          const user = response.data;
          const profile = user.profile || {};
          
          // Load saved addresses
          let addresses = addressService.getAddresses();
          
          // If no addresses exist, create one from profile
          if (addresses.length === 0 && (profile.address || profile.city || profile.state || profile.zipCode)) {
            const fullName = formatFullName(user);
            const profileAddress = {
              name: fullName,
              company: profile.clinicName || '',
              address1: profile.address || '',
              address2: '',
              city: profile.city || '',
              state: profile.state || '',
              zipCode: profile.zipCode || '',
              phone: user.phone || '',
              isDefault: true,
              label: 'Default Address'
            };
            
            const saveResult = addressService.saveAddress(profileAddress);
            if (saveResult.success) {
              addresses = [saveResult.address];
            }
          }
          
          setSavedAddresses(addresses);
          
          // Load default address if available
          const defaultAddress = addressService.getDefaultAddress();
          if (defaultAddress) {
            setSelectedShippingId(defaultAddress.id);
            setShippingAddress(defaultAddress);
            setUseNewShipping(false);
          } else if (profile.address || profile.city || profile.state || profile.zipCode) {
            // Use profile address if no saved addresses
            const fullName = formatFullName(user);
            setShippingAddress({
              name: fullName,
              company: profile.clinicName || '',
              address1: profile.address || '',
              address2: '',
              city: profile.city || '',
              state: profile.state || '',
              zipCode: profile.zipCode || '',
              phone: user.phone || currentUser?.phone || '',
            });
            setUseNewShipping(true);
          } else {
            setUseNewShipping(true);
          }
        } else {
          // Fallback to localStorage addresses
          const addresses = addressService.getAddresses();
          setSavedAddresses(addresses);
          const defaultAddress = addressService.getDefaultAddress();
          if (defaultAddress) {
            setSelectedShippingId(defaultAddress.id);
            setShippingAddress(defaultAddress);
            setUseNewShipping(false);
          } else {
            setUseNewShipping(true);
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fallback to localStorage addresses
        const addresses = addressService.getAddresses();
        setSavedAddresses(addresses);
        const defaultAddress = addressService.getDefaultAddress();
        if (defaultAddress) {
          setSelectedShippingId(defaultAddress.id);
          setShippingAddress(defaultAddress);
          setUseNewShipping(false);
        } else {
          setUseNewShipping(true);
        }
      }
    };
    
    syncProfileAddress();

    // Set initial delivery date
    const initialDate = getMinDeliveryDate();
    setDeliveryDate(initialDate);

    // Fetch vet contacts for ordering selection
    const loadVetContacts = async () => {
      if (!currentUser?.id) return;
      
      try {
        setLoadingContacts(true);
        const response = await clientContactsAPI.getContacts(currentUser.id);
        if (response.success && response.data) {
          // Filter to only show VET and VET_TECH roles
          const vets = response.data.filter(c => 
            c.role === 'VET' || c.role === 'VET_TECH'
          );
          setVetContacts(vets);
        }
      } catch (error) {
        console.error('Failed to load vet contacts:', error);
      } finally {
        setLoadingContacts(false);
      }
    };
    loadVetContacts();
  }, [cartItems, navigate, currentUser]);

  // Handle address selection
  const handleAddressSelection = (addressId) => {
    if (addressId === 'new') {
      setUseNewShipping(true);
      setSelectedShippingId('');
      setShippingAddress({
        name: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        phone: ''
      });
    } else {
      const selectedAddress = savedAddresses.find(addr => addr.id === addressId);
      if (selectedAddress) {
        setUseNewShipping(false);
        setSelectedShippingId(addressId);
        setShippingAddress(selectedAddress);
      }
    }
  };

  // Render shipping address form
  const renderShippingAddressForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Shipping Address
      </Typography>
      
      {/* Saved Addresses Selection */}
      {savedAddresses.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Choose Address
          </Typography>
          <RadioGroup
            value={useNewShipping ? 'new' : selectedShippingId}
            onChange={(e) => handleAddressSelection(e.target.value)}
          >
            {savedAddresses.map((address) => (
              <Paper key={address.id} sx={{ p: 2, mb: 1 }}>
                <FormControlLabel
                  value={address.id}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {address.label || 'Address'} {address.isDefault && '(Default)'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {address.name} - {address.address1}, {address.city}, {address.state} {address.zipCode}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            ))}
            <Paper sx={{ p: 2 }}>
              <FormControlLabel
                value="new"
                control={<Radio />}
                label="Use new address"
              />
            </Paper>
          </RadioGroup>
        </Box>
      )}
      
      {/* Address Form - Show when new address selected or no saved addresses */}
      {(useNewShipping || savedAddresses.length === 0) && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            {savedAddresses.length > 0 ? 'New Address' : 'Shipping Address'}
          </Typography>
          <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Full Name"
            value={shippingAddress.name}
            onChange={(e) => setShippingAddress({...shippingAddress, name: e.target.value})}
            error={!!errors.shippingName}
            helperText={errors.shippingName}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Company/Clinic Name"
            value={shippingAddress.company}
            onChange={(e) => setShippingAddress({...shippingAddress, company: e.target.value})}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Street Address"
            value={shippingAddress.address1}
            onChange={(e) => setShippingAddress({...shippingAddress, address1: e.target.value})}
            error={!!errors.shippingAddress1}
            helperText={errors.shippingAddress1}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Apartment, Suite, etc. (Optional)"
            value={shippingAddress.address2}
            onChange={(e) => setShippingAddress({...shippingAddress, address2: e.target.value})}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="City"
            value={shippingAddress.city}
            onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
            error={!!errors.shippingCity}
            helperText={errors.shippingCity}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="State"
            value={shippingAddress.state}
            onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
            error={!!errors.shippingState}
            helperText={errors.shippingState}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="ZIP Code"
            value={shippingAddress.zipCode}
            onChange={(e) => setShippingAddress({...shippingAddress, zipCode: e.target.value})}
            error={!!errors.shippingZipCode}
            helperText={errors.shippingZipCode}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone Number"
            value={shippingAddress.phone}
            onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
            error={!!errors.shippingPhone}
            helperText={errors.shippingPhone}
            placeholder="(555) 555-5555"
            required
          />
        </Grid>
          </Grid>
        </Box>
      )}

      {/* Ordering Vet Selection - Only show if there are vet contacts */}
      {vetContacts.length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Ordering For
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="ordering-vet-label">Select Ordering Vet</InputLabel>
            <Select
              labelId="ordering-vet-label"
              value={orderingVetContactId}
              onChange={(e) => setOrderingVetContactId(e.target.value)}
              label="Select Ordering Vet"
              disabled={loadingContacts}
            >
              <MenuItem value="">
                <em>General / Practice Stock Order</em>
              </MenuItem>
              {vetContacts.map((contact) => (
                <MenuItem key={contact.id} value={contact.id}>
                  {contact.prefix && `${contact.prefix} `}
                  {contact.firstName} {contact.lastName}
                  {contact.suffix && `, ${contact.suffix}`}
                  {contact.role === 'VET_TECH' && ' (Vet Tech)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which vet this order is for, or leave as "General" for practice stock.
          </Typography>
        </>
      )}

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Billing Address
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={billingSameAsShipping}
            onChange={(e) => setBillingSameAsShipping(e.target.checked)}
          />
        }
        label="Same as shipping address"
        sx={{ mb: 2 }}
      />

      {!billingSameAsShipping && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Full Name"
              value={billingAddress.name}
              onChange={(e) => setBillingAddress({...billingAddress, name: e.target.value})}
              error={!!errors.billingName}
              helperText={errors.billingName}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company/Clinic Name"
              value={billingAddress.company}
              onChange={(e) => setBillingAddress({...billingAddress, company: e.target.value})}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              value={billingAddress.address1}
              onChange={(e) => setBillingAddress({...billingAddress, address1: e.target.value})}
              error={!!errors.billingAddress1}
              helperText={errors.billingAddress1}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Apartment, Suite, etc. (Optional)"
              value={billingAddress.address2}
              onChange={(e) => setBillingAddress({...billingAddress, address2: e.target.value})}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="City"
              value={billingAddress.city}
              onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
              error={!!errors.billingCity}
              helperText={errors.billingCity}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="State"
              value={billingAddress.state}
              onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
              error={!!errors.billingState}
              helperText={errors.billingState}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="ZIP Code"
              value={billingAddress.zipCode}
              onChange={(e) => setBillingAddress({...billingAddress, zipCode: e.target.value})}
              error={!!errors.billingZipCode}
              helperText={errors.billingZipCode}
              required
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );

  // Render delivery and payment options
  const renderDeliveryAndPayment = () => (
    <Box>
      {/* Delivery Date */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Delivery Information
      </Typography>
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          type="date"
          label="Requested Delivery Date"
          value={deliveryDate}
          onChange={(e) => setDeliveryDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: getMinDeliveryDate()
          }}
          error={!!errors.deliveryDate}
          helperText={errors.deliveryDate || 'Next-day delivery not available after 3 PM EST'}
          required
        />
      </Box>

      {/* Shipping Method */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Shipping Method
      </Typography>
      <FormControl component="fieldset" fullWidth sx={{ mb: 4 }}>
        <RadioGroup
          value={shippingMethod}
          onChange={(e) => setShippingMethod(e.target.value)}
        >
          <Paper sx={{ p: 2, mb: 2, border: shippingMethod === 'FEDEX_FIRST_OVERNIGHT' ? '2px solid #1976d2' : '1px solid #e0e0e0' }}>
            <FormControlLabel
              value="FEDEX_FIRST_OVERNIGHT"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>FedEx First Overnight (FREE)</Typography>
                  <Typography variant="body2" color="text.secondary">By 8:30am delivery - we cover shipping costs</Typography>
                </Box>
              }
            />
          </Paper>
          <Paper sx={{ p: 2, mb: 2, border: shippingMethod === 'FEDEX_STANDARD_OVERNIGHT' ? '2px solid #1976d2' : '1px solid #e0e0e0' }}>
            <FormControlLabel
              value="FEDEX_STANDARD_OVERNIGHT"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>FedEx Standard Overnight (FREE)</Typography>
                  <Typography variant="body2" color="text.secondary">By 10:30am delivery - we cover shipping costs</Typography>
                </Box>
              }
            />
          </Paper>
          <Paper sx={{ p: 2, mb: 2, border: shippingMethod === 'UPS_NEXT_DAY_AIR_EARLY' ? '2px solid #1976d2' : '1px solid #e0e0e0' }}>
            <FormControlLabel
              value="UPS_NEXT_DAY_AIR_EARLY"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>UPS Next Day Air Early (FREE)</Typography>
                  <Typography variant="body2" color="text.secondary">By 9:00am delivery - we cover shipping costs</Typography>
                </Box>
              }
            />
          </Paper>
          <Paper sx={{ p: 2, border: shippingMethod === 'UPS_NEXT_DAY_AIR' ? '2px solid #1976d2' : '1px solid #e0e0e0' }}>
            <FormControlLabel
              value="UPS_NEXT_DAY_AIR"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>UPS Next Day Air (FREE)</Typography>
                  <Typography variant="body2" color="text.secondary">By 12:00pm delivery - we cover shipping costs</Typography>
                </Box>
              }
            />
          </Paper>
        </RadioGroup>
      </FormControl>

      {/* Payment Terms Information */}
      <Paper sx={{ p: 3, bgcolor: '#f0f9ff', border: '1px solid #0284c7', mt: 3 }}>
        <Typography variant="body1" fontWeight={600} color="#0c4a6e" gutterBottom>
          Net 60 Terms with Early Payment Discount
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          • Payment due 60 days from invoice date<br/>
          • <strong>10% early payment discount</strong> if paid within 2 days<br/>
          • Invoice will be created upon order confirmation
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0c4a6e' }}>
          Potential early payment savings: ${(totals.totalAmount * 0.1).toFixed(2)}
        </Typography>
      </Paper>
    </Box>
  );

  // Render order review
  const renderOrderReview = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Order Review
      </Typography>

      {/* Shipping Address */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShippingIcon /> Shipping Address
          </Typography>
          <Typography variant="body2">{shippingAddress.name}</Typography>
          {shippingAddress.company && <Typography variant="body2">{shippingAddress.company}</Typography>}
          <Typography variant="body2">{shippingAddress.address1}</Typography>
          {shippingAddress.address2 && <Typography variant="body2">{shippingAddress.address2}</Typography>}
          <Typography variant="body2">{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zipCode}</Typography>
          <Typography variant="body2">{shippingAddress.phone}</Typography>
        </CardContent>
      </Card>

      {/* Delivery & Shipping */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon /> Delivery Information
          </Typography>
          <Typography variant="body2">Requested Delivery: {new Date(deliveryDate).toLocaleDateString()}</Typography>
          <Typography variant="body2">
            Shipping Method: {shippingMethod === 'FEDEX_FIRST_OVERNIGHT' ? 'FedEx First Overnight (by 8:30am)' :
                              shippingMethod === 'FEDEX_STANDARD_OVERNIGHT' ? 'FedEx Standard Overnight (by 10:30am)' :
                              shippingMethod === 'UPS_NEXT_DAY_AIR_EARLY' ? 'UPS Next Day Air Early (by 9:00am)' :
                              shippingMethod === 'UPS_NEXT_DAY_AIR' ? 'UPS Next Day Air (by 12:00pm)' : shippingMethod}
          </Typography>
        </CardContent>
      </Card>

      {/* Payment Method */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon /> Payment Option
          </Typography>
          <Typography variant="body2">
            Net 60 Terms with 10% Early Payment Discount (2 days)
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Invoice will be created with Net 60 terms. Pay within 2 days for 10% discount!
          </Alert>
        </CardContent>
      </Card>

      {/* Order Items */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Order Items
            </Typography>
            {isBulkPricing && (
              <Chip 
                label="Bulk pricing applied" 
                size="small" 
                sx={{ 
                  backgroundColor: '#5c725c',
                  color: 'white'
                }} 
              />
            )}
          </Box>
          {cartItems.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="body2">{item.name} × {item.quantity}</Typography>
                <Typography variant="caption" color="text.secondary">
                  ${tieredPricePerUnit.toFixed(2)} each
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>${(tieredPricePerUnit * item.quantity).toFixed(2)}</Typography>
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  );

  // Render order summary sidebar
  const renderOrderSummary = () => (
    <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Order Summary
      </Typography>
      <Divider sx={{ my: 2 }} />
      
      {cartItems.map((item, index) => (
        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="body2">{item.name} × {item.quantity}</Typography>
            <Typography variant="caption" color="text.secondary">
              ${tieredPricePerUnit.toFixed(2)} each
            </Typography>
          </Box>
          <Typography variant="body2">${(tieredPricePerUnit * item.quantity).toFixed(2)}</Typography>
        </Box>
      ))}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2">Subtotal:</Typography>
        <Typography variant="body2">${totals.subtotal.toFixed(2)}</Typography>
      </Box>
      
      {promoApplied && promoDetails && totals.promoDiscount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="success.main">Promo Code ({promoCode}):</Typography>
          <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
            -${totals.promoDiscount.toFixed(2)}
          </Typography>
        </Box>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2">Shipping:</Typography>
        <Typography variant="body2" color="success.main">FREE</Typography>
      </Box>

      {totals.discountAmount > 0 && !promoApplied && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="success.main">Discount (5%):</Typography>
          <Typography variant="body2" color="success.main">-${totals.discountAmount.toFixed(2)}</Typography>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={600}>Total:</Typography>
        <Typography variant="h6" fontWeight={600} color="primary">${totals.totalAmount.toFixed(2)}</Typography>
      </Box>

      <Alert severity="success" sx={{ mt: 2 }}>
        <strong>Net 60 Terms:</strong> Invoice due in 60 days<br />
        <strong>Early Payment Discount:</strong> Pay within 2 days and save 10%<br />
        <strong>Potential savings: ${(totals.totalAmount * 0.1).toFixed(2)}</strong>
      </Alert>
    </Paper>
  );

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>Order Submitted Successfully!</Typography>
        <Typography variant="body1" color="text.secondary">
          Redirecting to confirmation page...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
        Checkout
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
        Complete your order information below
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            {activeStep === 0 && renderShippingAddressForm()}
            {activeStep === 1 && renderDeliveryAndPayment()}
            {activeStep === 2 && (
              <>
                {/* Promo Code Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Promo Code
                  </Typography>
                  
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Enter Promo Code"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="e.g., CASE25"
                        disabled={promoApplied || loading}
                        error={!!promoError}
                        helperText={promoError || 'Enter code exactly as provided'}
                        InputProps={{
                          endAdornment: promoApplied && <CheckCircleIcon color="success" />
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <Button
                        fullWidth
                        variant={promoApplied ? "outlined" : "contained"}
                        onClick={promoApplied ? handleRemovePromo : handleValidatePromo}
                        disabled={!promoCode || validatingPromo || loading}
                        startIcon={validatingPromo ? <CircularProgress size={20} /> : null}
                      >
                        {validatingPromo ? 'Validating...' : promoApplied ? 'Remove Code' : 'Apply Code'}
                      </Button>
                    </Grid>
                  </Grid>
                  
                  {promoApplied && promoDetails && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Promo Code Applied: {promoDetails.code}
                      </Typography>
                      {promoDetails.description && (
                        <Typography variant="body2">
                          {promoDetails.description}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mt: 1, color: 'success.main', fontWeight: 600 }}>
                        You're saving ${promoDetails.discountAmount.toFixed(2)}!
                      </Typography>
                      {promoDetails.freeQuantity > 0 && (
                        <Typography variant="body2">
                          + {promoDetails.freeQuantity} free item(s)
                        </Typography>
                      )}
                      {promoDetails.remainingUses !== null && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          You can use this code {promoDetails.remainingUses} more time(s)
                        </Typography>
                      )}
                    </Alert>
                  )}
                  <Divider sx={{ my: 3 }} />
                </Box>
                
                {renderOrderReview()}
              </>
            )}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmitOrder}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Submitting...' : 'Submit Order'}
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Order Summary Sidebar */}
        <Grid item xs={12} md={4}>
          {renderOrderSummary()}
        </Grid>
      </Grid>
    </Container>
  );
};

export default Checkout;
